import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
	Confirm,
	Input,
	InputType,
	Modal,
	Select,
	SpinnerOverlay,
	SubmissionButtons } from '@components/common';

import { useManageCellOptions } from '@hooks/useManageCellOptions';
import { useTechnologies } from '@hooks/useTechnologies';

import { prepareBandOptions } from '../helpers/prepareBandOptions';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { ICreateSectorsSettingsDTO, ISectorSettingDTO } from '@interfaces/map/dto/sectorsDTO';

import { ETechnologiesValues, TTechnologiesValues, TVendorsValues } from '@enums/common';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { modalCloseConfirm } from '@constants/modals';
import {
	BAND_FIELD,
	BEAMWIDTH_FIELD,
	RADIUS_FIELD,
	TECHNOLOGY_FIELD,
} from '../constants';
import { DEFAULT_COLOR_ID } from './constants';

import SectorColorSelector from '../SectorsSettingsForm/SectorColorSelector';
import { baseSchema } from './schema';

import styles from './SectorsSettingsForm.module.scss';

const SELECT_WIDTH = '240px';

export interface ISectorsSettingsProps {
	currentVendor: TVendorsValues;
	editingItem: ISectorSettingDTO | null;
	existingSectorSettings: ISectorSettingDTO[];
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	closeModal: () => void;
	isLoading?: boolean;
	submitHandler?: (data: ICreateSectorsSettingsDTO) => void;
}

const SectorsSettingsForm = ({
	closeModal,
	currentVendor,
	editingItem,
	isLoading,
	existingSectorSettings,
	setIsDirtyFormModal,
	submitHandler,
}: ISectorsSettingsProps) => {
	const { technologiesOptions, isLoading: isLoadingTechs } = useTechnologies();
	const {
		band4GFddOptions,
		band4GTddOptions,
		band5GOptions,
		isLoadingTechnologiesOptions,
	} = useManageCellOptions();

	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

	const [bandOptionsForSelectedTechnology, setBandOptionsForSelectedTechnology] = useState<TSelectOption[]>([]);

	const defaultTechnologyOption = useMemo(() => {
		return technologiesOptions.find(({ value }) => value === editingItem?.technology.name) || null;
	}, [editingItem?.technology.name, technologiesOptions]);

	const defaultBandOption = useMemo(() => {
		return bandOptionsForSelectedTechnology.find(option => option?.name === editingItem?.band.name) || null;
	}, [bandOptionsForSelectedTechnology, editingItem?.band.name]);

	const {
		control,
		formState: { isDirty, errors },
		handleSubmit,
		register,
		resetField,
		setValue,
		watch,
	} = useForm<FieldValues>({
		resolver: yupResolver(baseSchema),
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		defaultValues: {
			technology: defaultTechnologyOption,
			band: defaultBandOption,
			radius: editingItem?.radius,
			beamwidth: editingItem?.beamwidth,
			colorId: editingItem?.colorId || DEFAULT_COLOR_ID,
		},
	});

	const resetDependentFields = useCallback(() => {
		resetField(BAND_FIELD);
		resetField(RADIUS_FIELD);
		resetField(BEAMWIDTH_FIELD);
	}, [resetField]);

	const getBandOptionsForSelectedTechnology = useCallback((selectedTech: TTechnologiesValues) => {
		switch (selectedTech) {
			case ETechnologiesValues.T_4G_TDD:
				setBandOptionsForSelectedTechnology(
					prepareBandOptions(band4GTddOptions, existingSectorSettings, currentVendor),
				);
				resetDependentFields();
				break;
			case ETechnologiesValues.T_4G_FDD:
				setBandOptionsForSelectedTechnology(
					prepareBandOptions(band4GFddOptions, existingSectorSettings, currentVendor),
				);
				resetDependentFields();
				break;
			case ETechnologiesValues.T_5G:
				setBandOptionsForSelectedTechnology(
					prepareBandOptions(band5GOptions, existingSectorSettings, currentVendor),
				);
				resetDependentFields();
				break;
			default:
				setBandOptionsForSelectedTechnology([]);
				resetDependentFields();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [band4GFddOptions, band4GTddOptions, band5GOptions, resetDependentFields, existingSectorSettings]);

	const onSubmit = useCallback((values: FieldValues) => {
		const dto = {
			band: values.band?.name,
			beamwidth: +values.beamwidth,
			radius: +values.radius,
			technology: values.technology?.value,
			vendor: currentVendor,
			colorId: values.colorId,
		};

		submitHandler?.(dto);
	}, [currentVendor, submitHandler]);

	useEffect(() => {
		if (editingItem) {
			getBandOptionsForSelectedTechnology(defaultTechnologyOption?.name as TTechnologiesValues);
		}
	}, [defaultTechnologyOption?.name, editingItem, getBandOptionsForSelectedTechnology]);

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	useEffect(() => {
		const subscription = watch((value, { name }) => {
			if (name === TECHNOLOGY_FIELD) {
				getBandOptionsForSelectedTechnology(value[TECHNOLOGY_FIELD].value);
			}
		});

		return () => subscription.unsubscribe();
	}, [
		band4GFddOptions,
		band4GTddOptions,
		band5GOptions,
		resetDependentFields,
		resetField,
		getBandOptionsForSelectedTechnology,
		setValue,
		watch,
	]);

	const handleCloseModal = useCallback(() => {
		if (isDirty) setIsConfirmModalOpen(true);
		else closeModal();
	}, [closeModal, isDirty]);

	const closeConfirmModal = useCallback(() => setIsConfirmModalOpen(false), []);

	const handleColorChange = useCallback((colorId: string | number) => {
		setValue('colorId', colorId);
	}, [setValue]);

	useEffect(() => {
		if (editingItem) {
			setValue(BAND_FIELD, defaultBandOption);
		}
	}, [defaultBandOption, editingItem, setValue]);

	return (
		<div className={styles.cellsSettingsForm}>
			{(isLoading || isLoadingTechs) && <SpinnerOverlay />}
			<Select
				control={control}
				errorMessage={errors?.technology?.message}
				isLoading={isLoading}
				label='Technology'
				name={TECHNOLOGY_FIELD}
				options={technologiesOptions}
				placeholder='Select technology'
				required
				width={SELECT_WIDTH}
			/>
			<Select
				control={control}
				disabled={!watch(TECHNOLOGY_FIELD)}
				errorMessage={errors?.band?.message}
				isLoading={isLoadingTechnologiesOptions}
				label='Band'
				name={BAND_FIELD}
				options={bandOptionsForSelectedTechnology}
				placeholder='Select band'
				required
				width={SELECT_WIDTH}
			/>
			<Input
				errorMessage={errors?.radius?.message}
				label='Radius'
				name={RADIUS_FIELD}
				placeholder='Enter radius'
				register={register}
				required
				setValue={setValue}
				type={InputType.integer}
				withUseForms
			/>
			<Input
				errorMessage={errors?.beamwidth?.message}
				label='Beamwidth'
				name={BEAMWIDTH_FIELD}
				placeholder='Enter beamwidth'
				register={register}
				required
				setValue={setValue}
				type={InputType.integer}
				withUseForms
			/>
			<SectorColorSelector
				defaultColorId={editingItem?.colorId}
				onChange={handleColorChange}
			/>
			<SubmissionButtons
				negativeHandler={handleCloseModal}
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel='Save'
			/>
			<Modal
				isOpen={isConfirmModalOpen}
				onClose={closeConfirmModal}
			>
				<Confirm
					onClose={closeConfirmModal}
					onSubmit={closeModal}
					{...modalCloseConfirm}
				/>
			</Modal>
		</div>
	);
};

export default memo(SectorsSettingsForm);
